import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Skeleton } from "../components/ui/skeleton";
import { Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LaRedactionPage() {
  const { t } = useTranslation();
  const P = t("pages.redaction", { returnObjects: true });
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/public/authors`);
        setAuthors(res.data?.authors || []);
      } catch {
        setAuthors([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-secondary pt-20" data-testid="la-redaction-page">
      <section className="bg-primary text-white py-16">
        <div className="max-w-4xl mx-auto px-6">
          <p className="font-accent text-accent text-lg italic mb-3">{P.kicker}</p>
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">{P.title}</h1>
          <p className="text-white/80 leading-relaxed max-w-2xl">
            {P.introBefore}
            <Link to="/charte-editoriale" className="underline text-accent hover:text-accent/80">{P.introLink}</Link>
            {P.introAfter}
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-32 w-full" />)}
          </div>
        ) : authors.length === 0 ? (
          <div className="bg-white border border-border rounded-lg p-10 text-center" data-testid="redaction-empty">
            <Users className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-semibold text-primary mb-2">{P.emptyH}</h2>
            <p className="text-muted-foreground">{P.emptyP}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="redaction-grid">
            {authors.map((a, i) => {
              const name = a.display_name || a.public_alias || P.fallbackName;
              const initials = name.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
              return (
                <div key={a.id || i} className="bg-white border border-border rounded-lg p-6 flex items-start gap-4" data-testid={`redaction-author-${i}`}>
                  {a.public_avatar_url ? (
                    <img src={a.public_avatar_url} alt={name} className="w-16 h-16 rounded-full object-cover border border-border flex-shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-primary font-heading font-semibold flex-shrink-0">{initials}</div>
                  )}
                  <div className="min-w-0">
                    <p className="font-heading text-lg font-semibold text-primary">{name}</p>
                    {a.editorial_title && <p className="text-sm text-accent">{a.editorial_title}</p>}
                    {a.public_bio && <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{a.public_bio}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
